/**
 * File: TourOverlay.tsx
 * Path: apps/admin/src/modules/layout/components/TourOverlay.tsx
 * Description: Runs a tour. Everything on the page darkens except the thing
 * being explained (the sidebar stays lit), and a small box says what it is,
 * with Back, Next, Close and a way back to the tutorial list. A step on another page opens that page first. If a
 * step's target is not on the page (still loading, or hidden), the box shows
 * on its own instead of lighting something wrong.
 */

import React from "react";
import { createPortal } from "react-dom";
import { useTourOverlayController } from "@/modules/layout/hooks/useTourOverlayController";

export const TourOverlay: React.FC = () => {
  const { step, index, title, total, last, frame, hole, tipStyle, nextRef, next, back, stop, backToList } = useTourOverlayController();

  if (!step) return null;

  return createPortal(
    <>
      {frame && (
        <div aria-hidden className="fixed z-[60] overflow-hidden" style={{ top: frame.top, left: frame.left, width: frame.width, height: frame.height }}>
          {hole ? (
            <div className="absolute border-2 border-primary" style={{ ...hole, boxShadow: "0 0 0 9999px rgba(0,0,0,0.7)" }} />
          ) : (
            <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.7)" }} />
          )}
        </div>
      )}
      <div
        role="dialog"
        aria-label={`Guide: ${step.title}`}
        className="fixed z-[70] space-y-stack-sm border border-primary bg-card p-gutter-md text-sm shadow-2xl"
        style={tipStyle}
      >
        <p className="font-bold">{step.title}</p>
        <p className="text-muted-foreground">{step.text}</p>
        <div className="flex items-center justify-between gap-stack-sm">
          <span className="text-xs text-muted-foreground">
            {title ? `${title} · ` : ""}
            step {index + 1} of {total}
          </span>
          <span className="flex items-center gap-stack-xs">
            {index > 0 && (
              <button type="button" onClick={back} className="min-h-11 px-stack-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                Back
              </button>
            )}
            <button type="button" onClick={stop} className="min-h-11 px-stack-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              Close
            </button>
            <button
              ref={nextRef}
              type="button"
              onClick={next}
              className="min-h-11 rounded-none bg-primary px-gutter-sm font-bold text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {last ? "Done" : "Next ›"}
            </button>
          </span>
        </div>
        <button type="button" onClick={backToList} className="min-h-11 text-sm font-bold text-primary underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          Back to the tutorial list
        </button>
      </div>
    </>,
    document.body,
  );
};
